import { PackageOpen, type LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';

interface EmptyScreenProps {
  icon?: LucideIcon;
  title?: string;
  description?: string;
  text?: string; // Fallback for backward compatibility
  className?: string;
  size?: 'small' | 'large';
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyScreen({
  icon: Icon = PackageOpen,
  title,
  description,
  text,
  className,
  size = 'large',
  action,
}: EmptyScreenProps) {
  const displayTitle = title || 'No data found';
  const displayDescription =
    description ||
    text ||
    "We couldn't find any items to display here at the moment.";

  const isSmall = size === 'small';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center animate-in fade-in duration-500',
        isSmall ? 'py-4' : 'py-12',
        className
      )}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="flex flex-col items-center max-w-md"
      >
        {/* Visual Illustration */}
        <div className={cn('relative', isSmall ? 'mb-2' : 'mb-8')}>
          {/* Decorative background shapes */}
          <div className="absolute inset-0 bg-teal/10 rounded-full blur-3xl -z-10 animate-pulse" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-teal/5 rounded-full blur-2xl -z-10 translate-x-1/2 -translate-y-1/2" />

          <div className="w-full rounded-lg bg-transparent flex items-center justify-center">
            <img
              src="/emptyscreen.svg"
              className={cn(
                'w-auto object-contain',
                isSmall ? 'h-40' : 'h-72 lg:h-96'
              )}
              alt="empty-screen"
            />
          </div>

          {/* Floating mini icons for detail - only show for large */}
          {!isSmall && (
            <div className="absolute bottom-16 right-20 w-10 h-10 rounded-full bg-white shadow-lg border border-border flex items-center justify-center animate-bounce duration-1000">
              <div className="w-2 h-2 rounded-full bg-teal animate-ping" />
            </div>
          )}
        </div>

        <div className="space-y-1">
          <h3
            className={cn(
              'font-bold text-text',
              isSmall ? 'text-sm' : 'text-xl'
            )}
          >
            {displayTitle}
          </h3>
          <p
            className={cn(
              'text-gray-500',
              isSmall ? 'text-[10px] leading-tight' : 'text-sm'
            )}
          >
            {displayDescription}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
